import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Facebook, Twitter, Linkedin, Instagram, Menu, X } from 'lucide-react';
import { companyInfo } from '../utils/mockData';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [coverageOpen, setCoverageOpen] = React.useState(false);
  const [mobileCoverageOpen, setMobileCoverageOpen] = React.useState(false);

  const coverageLinks = [
    { to: "/life-insurance", label: "Life Insurance" },
    { to: "/final-expense", label: "Final Expense" },
    { to: "/health-supplemental", label: "Health & Supplemental" },
    { to: "/medicare", label: "Medicare" },
    { to: "/iuls", label: "IULs" },
    { to: "/annuities", label: "Annuities" },
    { to: "/dental", label: "Dental" },
    { to: "/vision", label: "Vision" },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-brand-blue text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-brand-orange transition">
                <Mail size={16} />
                <span className="hidden sm:inline">{companyInfo.email}</span>
              </a>
              <a href={`tel:${companyInfo.phoneRaw}`} className="flex items-center gap-2 hover:text-brand-orange transition">
                <Phone size={16} />
                <span>{companyInfo.phone}</span>
              </a>
            </div>
            <div className="flex items-center gap-1">
              <a href={companyInfo.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-brand-orange transition">
                <Facebook size={16} />
              </a>
              <a href={companyInfo.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-brand-orange transition">
                <Twitter size={16} />
              </a>
              <a href={companyInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-brand-orange transition">
                <Linkedin size={16} />
              </a>
              <a href={companyInfo.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-brand-orange transition">
                <Instagram size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="w-full px-4">
          <div className="flex items-center justify-between lg:justify-center gap-3 lg:gap-5 xl:gap-8 py-1">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_df9cc6b4-4b60-4b5a-88ef-edcbe49cc232/artifacts/tfyf8r6v_purposefully_covered_insurance_group_logo_v1_transparent.png" 
                alt="Purposefully Covered Insurance Group" 
                className="h-24 w-auto scale-[1.08] origin-center"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-4 xl:gap-7" data-testid="desktop-nav">
              <Link to="/" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">Home</Link>
              
              <div
                className="relative"
                onMouseEnter={() => setCoverageOpen(true)}
                onMouseLeave={() => setCoverageOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setCoverageOpen((o) => !o)}
                  className="text-gray-700 hover:text-brand-blue font-medium transition flex items-center gap-1 whitespace-nowrap"
                  data-testid="coverage-dropdown-btn"
                >
                  Coverage
                  <svg className={`w-4 h-4 transition-transform ${coverageOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className={`absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-56 transition-all duration-200 ${coverageOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                  {coverageLinks.map(link => (
                    <Link key={link.to} to={link.to} onClick={() => setCoverageOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-brand-blue hover:text-white transition" data-testid={`nav-${link.to.slice(1)}`}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/about" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">About</Link>
              <Link to="/faq" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">FAQ</Link>
              <Link to="/testimonials" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">Testimonials</Link>
              <Link to="/contact" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">Contact</Link>
              <Link to="/careers" className="text-gray-700 hover:text-brand-blue font-medium transition whitespace-nowrap">Careers</Link>
              <Link to="/agent/login" className="text-gray-700 hover:text-brand-blue font-medium transition text-sm whitespace-nowrap">Agent Portal</Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <a href={`tel:${companyInfo.phoneRaw}`} className="flex items-center gap-2 text-brand-blue font-semibold hover:text-brand-orange transition whitespace-nowrap" data-testid="header-call-btn">
                <Phone size={18} />
                <span className="text-sm whitespace-nowrap">{companyInfo.phone}</span>
              </a>
              <Link to="/#quote" onClick={() => { setTimeout(() => { const el = document.getElementById('quote'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }} className="bg-brand-orange text-white px-5 py-2.5 rounded-md hover:bg-brand-blue transition font-semibold text-sm whitespace-nowrap" data-testid="header-quote-btn">
                Get My Free Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-900 min-w-[48px] min-h-[44px] flex items-center justify-center rounded-lg bg-gray-100 border border-gray-300 hover:bg-gray-200 transition shadow-sm"
              data-testid="mobile-nav-toggle"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200" style={{ animation: 'slideDown 0.2s ease-out' }} data-testid="mobile-menu">
              <nav className="flex flex-col divide-y divide-gray-100">
                <Link to="/" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>Home</Link>

                {/* Coverage (collapsible) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setMobileCoverageOpen((o) => !o)}
                    className="w-full px-1 py-4 min-h-[48px] flex items-center justify-between text-gray-800 font-semibold hover:text-brand-blue"
                    data-testid="mobile-coverage-toggle"
                    aria-expanded={mobileCoverageOpen}
                  >
                    Coverage
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${mobileCoverageOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileCoverageOpen && (
                    <div className="flex flex-col bg-gray-50 -mx-4 px-4 border-t border-gray-100">
                      {coverageLinks.map(link => (
                        <Link key={link.to} to={link.to} className="pl-4 py-3 min-h-[44px] flex items-center text-gray-600 hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/about" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>About</Link>
                <Link to="/faq" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
                <Link to="/testimonials" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>Testimonials</Link>
                <Link to="/contact" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <Link to="/careers" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>Careers</Link>
                <Link to="/agent/login" className="px-1 py-4 min-h-[48px] flex items-center text-gray-800 font-semibold hover:text-brand-blue" onClick={() => setMobileMenuOpen(false)}>Agent Portal</Link>
              </nav>
              <div className="flex flex-col gap-2 py-4">
                <a href={`tel:${companyInfo.phoneRaw}`} className="bg-brand-blue text-white px-6 py-3 rounded-md font-semibold text-center flex items-center justify-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                  <Phone size={18} /> Call {companyInfo.phone}
                </a>
                <Link to="/#quote" className="bg-brand-orange text-white px-6 py-3 rounded-md hover:bg-brand-orange-light transition font-semibold text-center" onClick={() => setMobileMenuOpen(false)}>Get My Free Quote</Link>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
